import { BACKEND_API_HOST } from '@/app/config';

export type MeetingKind = 'lve' | 'previsit' | 'revisit' | 'other';
export type HostRole = 'estimator' | 'installer';

export interface MeetingContext {
  kind: MeetingKind;
  hostRole: HostRole;
}

const KINDS: MeetingKind[] = ['lve', 'previsit', 'revisit', 'other'];
const HOST_ROLES: HostRole[] = ['estimator', 'installer'];

export const MEETING_CONTEXT_TIME_LIMIT_MS = 3000;

const known = <T extends string>(values: T[], value: unknown) => values.find(candidate => candidate === value);

const toContext = (body: { kind?: unknown; host_role?: unknown } | null): MeetingContext | null => {
  const kind = known(KINDS, body?.kind);
  const hostRole = known(HOST_ROLES, body?.host_role);
  if (!kind || !hostRole) {
    return null;
  }

  return { kind, hostRole };
};

// Null is a final "no kind" (404, unreadable body); a failure that may pass throws.
const fetchMeetingContext = async(meetingHash: string): Promise<MeetingContext | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MEETING_CONTEXT_TIME_LIMIT_MS);
  try {
    const response = await fetch(
      `${BACKEND_API_HOST}/api/schedule/video-meeting/${meetingHash}/context/`,
      { signal: controller.signal },
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Meeting context answered ${response.status}`);
    }
    return toContext(await response.json().catch(() => null));
  } finally {
    clearTimeout(timer);
  }
};

export default fetchMeetingContext;
